/**
 * Three.js renderer hosting the Bioclast scene.
 *
 * Public API mirrors the old WebGL2 renderer so `main.ts` and `controls.ts`
 * keep working: `setFeatures`, `start`, `stop`, `params`, `frameMs`,
 * `captureStream`, `snapshot`.
 */

import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

import { AudioFeatures, ZERO_FEATURES } from "../audio/types";
import {
  BioclastScene,
  BioclastParams,
  DEFAULT_BIOCLAST,
  type BioclastDiagnostics,
} from "./scenes/bioclast";
import { FeedbackPass } from "./feedbackPass";
import { PostFxPass } from "./postFxPass";
import {
  AltSceneApi,
  AltSceneType,
  createAltScene,
} from "./altScenes";
import { applyModulations, ModulationMap } from "./modulations";

export type SceneType = "bioclast" | AltSceneType;

export type RendererParams = BioclastParams;
export type OutputProjection = "flat" | "curved" | "cylinder" | "sphere";
export type CameraControlMode = "auto" | "free" | "fly";
export interface CameraSyncState {
  mode: CameraControlMode;
  orbit: number;
  tilt: number;
  zoom: number;
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
  shot: number;
  coverage: number;
}

type SceneCameraDefaults = Pick<BioclastParams,
  "cameraMode" | "cameraOrbit" | "cameraTilt" | "cameraZoom" | "cameraSpin" |
  "camAutoRotateH" | "camAutoRotateV" | "camAutoRoll" | "flythroughSpeed" | "beatDolly"
>;

export interface RendererDiagnostics {
  rendererPixelRatio: number;
  composerPixelRatio: number;
  composerActive: boolean;
  feedbackPassEnabled: boolean;
  postFxPassEnabled: boolean;
  altScene?: {
    backend: string;
    particleCount: number;
    look?: string;
    spatial?: {
      centroidRadius: number;
      spread: [number, number, number];
      radialMean: number;
      radialStdDev: number;
      occupiedCellRatio: number;
      maxCellShare: number;
    };
  };
}

export class Renderer {
  private gl: THREE.WebGLRenderer;
  private scene: BioclastScene;
  private rafId: number | null = null;
  private frameTimer: ReturnType<typeof setTimeout> | null = null;
  private features: AudioFeatures = { ...ZERO_FEATURES };
  private hasSmoothedFeatures = false;
  private lastFeatureAt = performance.now();

  // Post-processing pipeline (composer renders scene → bloom → feedback → output).
  private composer: EffectComposer;
  private renderPass: RenderPass;
  private bloomPass: UnrealBloomPass;
  private feedbackPass: FeedbackPass;
  private postFxPass: PostFxPass;
  private outputPass: OutputPass;

  /** Look + reactivity parameters. UI mutates this directly. */
  public params: RendererParams = { ...DEFAULT_BIOCLAST };

  /** EMA frame time in ms — UI / adaptive quality can read this. */
  public frameMs = 16.7;

  /** Active scene type. Defaults to the legacy Bioclast galaxy. */
  public sceneType: SceneType = "bioclast";
  private altScene: AltSceneApi | null = null;
  private altSceneType: AltSceneType | null = null;
  private altCamBasePos: THREE.Vector3 | null = null;
  private altCamBaseFov: number | null = null;
  private altCamElapsed = 0;
  private readonly altCamTarget = new THREE.Vector3();
  private readonly altCamDesired = new THREE.Vector3();
  private cameraControlMode: CameraControlMode = "auto";
  private autoCameraShotIndex = 0;
  private autoCameraShotChangedAt = performance.now();
  private lastCameraTelemetryAt = 0;
  private outputProjection: OutputProjection = "flat";
  private outputView = { x: 0, y: 0, width: 1, height: 1 };
  private outputFill = 1;
  private outputFillY = 1;
  private readonly freeCameraPosition = new THREE.Vector3();
  private readonly freeCameraTarget = new THREE.Vector3();
  private freeCameraFov = 62;
  private freeCameraReady = false;
  private sceneCameraDefaults: SceneCameraDefaults;
  /** Optional per-preset framing override (vizz.fm cameraState). */
  private altCamOverride: { position: THREE.Vector3; target: THREE.Vector3; quaternion: THREE.Quaternion } | null = null;

  /** UI hook for vizz.fm-style camera framing state changes. */
  public onAltCameraOverrideChange: ((locked: boolean) => void) | null = null;
  public onCameraChange: ((state: CameraSyncState) => void) | null = null;

  /** Modulation routing map (vizz.fm-style). */
  public modulations: ModulationMap = {};
  /** Snapshot of the original (pre-modulated) param values, restored each frame. */
  private paramSnapshot: Partial<BioclastParams> = {};

  private lastFrameAt = performance.now();
  private startedAt = performance.now();
  private visualSyncEpochSeconds: number | null = null;
  private dprCap = 2;
  private composerPixelRatio = 1;
  private lastUseComposer = false;
  private disposed = false;
  private readonly cleanupCallbacks: Array<() => void> = [];
  private readonly onResize = (): void => this.resize();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.gl = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: true,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true, // needed for snapshot()
      powerPreference: "high-performance",
    });
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.dprCap));
    this.gl.setClearColor(0x000000, 0);
    this.gl.outputColorSpace = THREE.SRGBColorSpace;
    this.gl.toneMapping = THREE.ACESFilmicToneMapping;
    this.gl.toneMappingExposure = 0.92;

    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    this.scene = new BioclastScene(w, h);
    this.scene.params = this.params;
    this.sceneCameraDefaults = this.readSceneCameraDefaults();

    // ---- Post-processing pipeline ----
    // RenderPass → UnrealBloomPass (selective glow on bright pixels) → OutputPass.
    // OutputPass handles tonemapping + sRGB conversion since EffectComposer's
    // intermediate render targets are linear-space.
    this.composer = new EffectComposer(this.gl);
    this.renderPass = new RenderPass(this.scene.scene, this.scene.camera);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      this.params.bloomStrength,
      this.params.bloomRadius,
      this.params.bloomThreshold,
    );
    // Feedback (frame-history trail effect) sits AFTER bloom so trails
    // inherit the glow. With strength=0 the pass is essentially a
    // passthrough — costs ~one extra fullscreen draw per frame.
    this.feedbackPass = new FeedbackPass(w, h);
    this.outputPass = new OutputPass();
    this.postFxPass = new PostFxPass(w, h);
    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(this.feedbackPass);
    // Post-FX stack runs AFTER bloom + feedback so distortions/CRT/grain
    // overlay the glow + trails like a finishing filter.
    this.composer.addPass(this.postFxPass);
    this.composer.addPass(this.outputPass);

    this.resize();
    window.addEventListener("resize", this.onResize, { passive: true });
    this.cleanupCallbacks.push(() => window.removeEventListener("resize", this.onResize));

    this.attachPointerControls();
  }

  /**
   * Mouse / touch drag rotates the camera; horizontal drag changes
   * `cameraOrbit` (yaw), vertical drag changes `cameraTilt`. Mouse wheel
   * adjusts `cameraZoom`. Two-finger pinch on touch devices also zooms.
   * All three knobs are also exposed as sliders, so users can pick whichever
   * feels better.
   */
  private attachPointerControls(): void {
    let dragging = false;
    let panning = false;
    let lastX = 0;
    let lastY = 0;
    const canvas = this.canvas;
    canvas.style.cursor = "grab";

    // Track active pointers so we can recognize a 2-finger pinch.
    const activePointers = new Map<number, { x: number; y: number }>();
    let pinchStartDist = 0;
    let pinchStartCameraDistance = 1;

    const distanceBetweenPointers = (): number => {
      const pts = [...activePointers.values()];
      if (pts.length < 2) return 0;
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    };

    const onDown = (ev: PointerEvent): void => {
      if (this.cameraControlMode !== "free") this.setCameraControlMode("free");
      activePointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      try {
        canvas.setPointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      if (activePointers.size === 1) {
        dragging = true;
        panning = ev.shiftKey || ev.button === 1 || ev.button === 2;
        lastX = ev.clientX;
        lastY = ev.clientY;
        canvas.style.cursor = panning ? "move" : "grabbing";
      } else if (activePointers.size === 2) {
        // Switch from drag to pinch — capture the starting distance + zoom
        // so we can scale relative to it.
        dragging = false;
        pinchStartDist = distanceBetweenPointers();
        pinchStartCameraDistance = this.freeCameraPosition.distanceTo(this.freeCameraTarget);
        canvas.style.cursor = "ew-resize";
      }
    };
    const onMove = (ev: PointerEvent): void => {
      if (!activePointers.has(ev.pointerId)) return;
      activePointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

      if (activePointers.size >= 2 && pinchStartDist > 0) {
        const dist = distanceBetweenPointers();
        const ratio = Math.max(0.05, dist / pinchStartDist);
        this.setFreeCameraDistance(pinchStartCameraDistance / ratio);
        this.emitCameraChange();
        return;
      }

      if (!dragging) return;
      const dx = ev.clientX - lastX;
      const dy = ev.clientY - lastY;
      lastX = ev.clientX;
      lastY = ev.clientY;
      if (panning) this.panFreeCamera(dx, dy);
      else this.orbitFreeCamera(dx, dy);
      this.emitCameraChange();
    };
    const onUp = (ev: PointerEvent): void => {
      activePointers.delete(ev.pointerId);
      try {
        canvas.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      if (activePointers.size === 0) {
        dragging = false;
        panning = false;
        canvas.style.cursor = "grab";
      } else if (activePointers.size === 1) {
        // Returning to single-finger drag — re-anchor lastX/Y from the
        // remaining pointer so we don't jump.
        const remaining = [...activePointers.values()][0];
        lastX = remaining.x;
        lastY = remaining.y;
        dragging = true;
        panning = false;
        pinchStartDist = 0;
        canvas.style.cursor = "grabbing";
      }
    };
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    const onContextMenu = (event: MouseEvent): void => event.preventDefault();
    canvas.addEventListener("contextmenu", onContextMenu);

    const onWheel = (ev: WheelEvent): void => {
      ev.preventDefault();
      if (this.cameraControlMode !== "free") this.setCameraControlMode("free");
      const distance = this.freeCameraPosition.distanceTo(this.freeCameraTarget);
      this.setFreeCameraDistance(distance * Math.exp(ev.deltaY * 0.0015));
      this.emitCameraChange();
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    this.cleanupCallbacks.push(() => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.removeEventListener("contextmenu", onContextMenu);
      canvas.removeEventListener("wheel", onWheel);
    });
    canvas.style.touchAction = "none";
  }

  private activeCamera(): THREE.PerspectiveCamera {
    return this.altScene?.camera ?? this.scene.camera;
  }

  private captureFreeCamera(): void {
    const camera = this.activeCamera();
    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);
    const distance = Math.max(1, camera.position.length());
    this.freeCameraPosition.copy(camera.position);
    this.freeCameraTarget.copy(camera.position).addScaledVector(forward, distance);
    this.freeCameraFov = camera.fov;
    this.freeCameraReady = true;
  }

  private orbitFreeCamera(dx: number, dy: number): void {
    const offset = this.freeCameraPosition.clone().sub(this.freeCameraTarget);
    const spherical = new THREE.Spherical().setFromVector3(offset);
    spherical.theta -= dx * 0.005;
    spherical.phi = THREE.MathUtils.clamp(spherical.phi + dy * 0.005, 0.01, Math.PI - 0.01);
    this.freeCameraPosition.copy(this.freeCameraTarget).add(offset.setFromSpherical(spherical));
  }

  private panFreeCamera(dx: number, dy: number): void {
    const camera = this.activeCamera();
    camera.position.copy(this.freeCameraPosition);
    camera.lookAt(this.freeCameraTarget);
    camera.updateMatrixWorld();
    const distance = Math.max(1, this.freeCameraPosition.distanceTo(this.freeCameraTarget));
    const scale = distance * 0.0015;
    const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).multiplyScalar(-dx * scale);
    const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).multiplyScalar(dy * scale);
    this.freeCameraPosition.add(right).add(up);
    this.freeCameraTarget.add(right).add(up);
  }

  private setFreeCameraDistance(distance: number): void {
    const offset = this.freeCameraPosition.clone().sub(this.freeCameraTarget);
    if (offset.lengthSq() < 0.0001) offset.set(0, 0, 1);
    const camera = this.activeCamera();
    const minimum = Math.max(0.02, camera.near * 1.25);
    const maximum = Math.max(minimum, camera.far * 0.9);
    offset.setLength(THREE.MathUtils.clamp(distance, minimum, maximum));
    this.freeCameraPosition.copy(this.freeCameraTarget).add(offset);
  }

  private applyOutputTileView(camera: THREE.PerspectiveCamera): void {
    if (
      Math.abs(this.outputView.x) < 0.001 &&
      Math.abs(this.outputView.y) < 0.001 &&
      Math.abs(this.outputView.width - 1) < 0.001 &&
      Math.abs(this.outputView.height - 1) < 0.001
    ) {
      camera.clearViewOffset();
      return;
    }
    const width = Math.max(1, this.canvas.clientWidth || window.innerWidth);
    const height = Math.max(1, this.canvas.clientHeight || window.innerHeight);
    const wallWidth = width / this.outputView.width;
    const wallHeight = height / this.outputView.height;
    camera.setViewOffset(
      wallWidth,
      wallHeight,
      wallWidth * this.outputView.x,
      wallHeight * this.outputView.y,
      width,
      height,
    );
  }

  private applyFreeCamera(): void {
    if (!this.freeCameraReady) this.captureFreeCamera();
    const camera = this.activeCamera();
    camera.position.copy(this.freeCameraPosition);
    camera.fov = this.freeCameraFov;
    camera.lookAt(this.freeCameraTarget);
    camera.updateProjectionMatrix();
  }

  private resize(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    const virtualWidth = w / this.outputView.width;
    const virtualHeight = h / this.outputView.height;
    this.gl.setSize(w, h, false);
    // Keep the render target one panel large, but frame the scene using the
    // full virtual wall. PostFxPass crops that virtual image to this panel.
    this.scene.setSize(virtualWidth, virtualHeight);
    this.altScene?.setSize(virtualWidth, virtualHeight);
    if (this.composer) {
      this.composerPixelRatio = this.gl.getPixelRatio();
      this.composer.setPixelRatio(this.composerPixelRatio);
      this.composer.setSize(w, h);
      this.bloomPass.setSize(w, h);
      this.feedbackPass.setSize(w, h);
      this.postFxPass.setSize(w, h);
    }
  }

  private adaptQuality(): void {
    if (this.frameMs > 30 && this.dprCap > 1) {
      this.dprCap = 1;
      this.gl.setPixelRatio(1);
      this.resize();
    }
  }

  setPixelRatioCap(cap: number): void {
    const nextCap = THREE.MathUtils.clamp(cap, 0.5, 2);
    if (nextCap === this.dprCap) return;
    this.dprCap = nextCap;
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.dprCap));
    this.resize();
  }

  setOutputProjection(mode: OutputProjection, warp: number, fov?: number): void {
    this.outputProjection = mode;
    this.postFxPass.setOutputProjection(mode, warp, fov);
    if (this.cameraControlMode === "auto") this.applyAutoCameraShot();
  }

  setOutputBrightness(brightness: number): void {
    this.postFxPass.setOutputBrightness(brightness);
  }

  setOutputCalibration(enabled: boolean): void {
    this.postFxPass.setOutputCalibration(enabled);
  }

  setOutputTile(tileX: number, tileY: number, columns: number, rows: number): void {
    const safeColumns = Math.max(1, Math.min(8, Math.round(columns)));
    const safeRows = Math.max(1, Math.min(8, Math.round(rows)));
    const safeX = Math.max(0, Math.min(safeColumns - 1, Math.round(tileX)));
    const safeY = Math.max(0, Math.min(safeRows - 1, Math.round(tileY)));
    this.setOutputView(
      safeX / safeColumns,
      safeY / safeRows,
      1 / safeColumns,
      1 / safeRows,
    );
  }

  setOutputView(viewX: number, viewY: number, viewWidth: number, viewHeight: number): void {
    const width = Math.max(0.01, Math.min(1, viewWidth));
    const height = Math.max(0.01, Math.min(1, viewHeight));
    this.outputView = {
      x: Math.max(0, Math.min(1 - width, viewX)),
      y: Math.max(0, Math.min(1 - height, viewY)),
      width,
      height,
    };
    this.postFxPass.setOutputView(
      this.outputView.x,
      this.outputView.y,
      this.outputView.width,
      this.outputView.height,
    );
    this.resize();
  }

  setOutputFill(fill: number): void {
    this.outputFill = Math.max(1, Math.min(3.5, fill));
  }

  setOutputFillY(fill: number): void {
    this.outputFillY = Math.max(1, Math.min(3.5, fill));
  }

  setVisualSyncEpoch(epochSeconds: number | null): void {
    this.visualSyncEpochSeconds = epochSeconds !== null && Number.isFinite(epochSeconds)
      ? epochSeconds
      : null;
  }

  private readSceneCameraDefaults(): SceneCameraDefaults {
    const p = this.params;
    return {
      cameraMode: p.cameraMode,
      cameraOrbit: p.cameraOrbit,
      cameraTilt: p.cameraTilt,
      cameraZoom: p.cameraZoom,
      cameraSpin: p.cameraSpin,
      camAutoRotateH: p.camAutoRotateH,
      camAutoRotateV: p.camAutoRotateV,
      camAutoRoll: p.camAutoRoll,
      flythroughSpeed: p.flythroughSpeed,
      beatDolly: p.beatDolly,
    };
  }

  onScenePresetApplied(): void {
    this.sceneCameraDefaults = this.readSceneCameraDefaults();
    this.autoCameraShotIndex = 0;
    this.autoCameraShotChangedAt = performance.now();
    this.applyCameraModePolicy();
  }

  setCameraControlMode(mode: CameraControlMode): void {
    if (mode === this.cameraControlMode) return;
    if (mode === "free") this.captureFreeCamera();
    this.cameraControlMode = mode;
    this.applyCameraModePolicy();
    this.emitCameraChange();
  }

  advanceAutoCameraShot(): void {
    this.setCameraControlMode("auto");
    this.autoCameraShotIndex = (this.autoCameraShotIndex + 1) % 4;
    this.autoCameraShotChangedAt = performance.now();
    this.applyAutoCameraShot();
    this.emitCameraChange();
  }

  private applyAutoCameraShot(): void {
    const shots = [
      { orbit: 0, tilt: 0, zoom: 1 },
      { orbit: Math.PI * 0.5, tilt: -0.18, zoom: 1.12 },
      { orbit: Math.PI, tilt: 0.2, zoom: 0.9 },
      { orbit: Math.PI * 1.5, tilt: -0.08, zoom: 1.25 },
    ];
    const shot = shots[this.autoCameraShotIndex];
    const projectionMargin = this.outputProjection === "sphere"
      ? 1.18
      : this.outputProjection === "cylinder" ? 1.1 : this.outputProjection === "curved" ? 1.05 : 1;
    this.params.cameraOrbit = this.sceneCameraDefaults.cameraOrbit + shot.orbit;
    this.params.cameraTilt = THREE.MathUtils.clamp(
      this.sceneCameraDefaults.cameraTilt + shot.tilt,
      0,
      1,
    );
    this.params.cameraZoom = THREE.MathUtils.clamp(
      this.sceneCameraDefaults.cameraZoom * shot.zoom / projectionMargin,
      0.15,
      3.5,
    );
    this.altCamElapsed = 0;
  }

  private updateAutoDirector(now: number): void {
    if (this.cameraControlMode !== "auto") return;
    const age = now - this.autoCameraShotChangedAt;
    if (age < 12000) return;
    const strongBeat = this.features.beat > 0.75 && this.features.bass > 0.45;
    if (strongBeat || age >= 16000) this.advanceAutoCameraShot();
  }

  restartFlyCamera(): void {
    this.setCameraControlMode("fly");
    this.altCamElapsed = 0;
    this.scene.restartFlythroughCamera();
    this.emitCameraChange();
  }

  private applyCameraModePolicy(): void {
    if (this.cameraControlMode === "auto") {
      Object.assign(this.params, this.sceneCameraDefaults);
      this.applyAutoCameraShot();
      return;
    }
    this.clearAltCameraOverride();
    if (this.cameraControlMode === "fly") {
      this.params.cameraMode = "flythrough";
      if (this.params.flythroughSpeed < 0.1) this.params.flythroughSpeed = 1;
      return;
    }
    this.params.cameraMode = "orbit";
    this.params.cameraSpin = 0;
    this.params.camAutoRotateH = 0;
    this.params.camAutoRotateV = 0;
    this.params.camAutoRoll = 0;
  }

  getCameraState(): CameraSyncState {
    const camera = this.activeCamera();
    const target = this.cameraControlMode === "free"
      ? this.freeCameraTarget
      : camera.position.clone().add(
        camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(Math.max(1, camera.position.length())),
      );
    return {
      mode: this.cameraControlMode,
      orbit: this.params.cameraOrbit,
      tilt: this.params.cameraTilt,
      zoom: this.params.cameraZoom,
      position: camera.position.toArray() as [number, number, number],
      target: target.toArray() as [number, number, number],
      fov: camera.fov,
      shot: this.autoCameraShotIndex,
      coverage: THREE.MathUtils.clamp(
        this.params.cameraZoom / Math.max(0.15, this.sceneCameraDefaults.cameraZoom) * 0.72,
        0,
        1,
      ),
    };
  }

  setCameraState(state: Partial<CameraSyncState>): void {
    if (state.mode === "auto" || state.mode === "free" || state.mode === "fly") {
      this.setCameraControlMode(state.mode);
    }
    if (Array.isArray(state.position) && state.position.length >= 3 &&
        Array.isArray(state.target) && state.target.length >= 3) {
      this.freeCameraPosition.fromArray(state.position);
      this.freeCameraTarget.fromArray(state.target);
      const fov = Number(state.fov);
      if (Number.isFinite(fov)) this.freeCameraFov = THREE.MathUtils.clamp(fov, 20, 110);
      this.freeCameraReady = true;
      if (state.mode === undefined) this.setCameraControlMode("free");
    }
    const orbit = Number(state.orbit);
    const tilt = Number(state.tilt);
    const zoom = Number(state.zoom);
    if (Number.isFinite(orbit)) this.params.cameraOrbit = orbit;
    if (Number.isFinite(tilt)) this.params.cameraTilt = THREE.MathUtils.clamp(tilt, 0, 1);
    if (Number.isFinite(zoom)) this.params.cameraZoom = THREE.MathUtils.clamp(zoom, 0.15, 3.5);
    if (this.altCamOverride) this.clearAltCameraOverride();
  }

  private emitCameraChange(): void {
    this.onCameraChange?.(this.getCameraState());
  }

  private isCurrentPostFxActive(): boolean {
    if (this.postFxPass.hasOutputAdjustment()) return true;
    const p = this.params;
    return p.postFxEnabled && (
      p.fxRgbShift > 0.001 || p.fxGlitch > 0.001 || p.fxCrt > 0.001 ||
      p.fxKaleidoscope > 0.001 || p.fxPixelate > 0.001 || p.fxDither > 0.001 ||
      p.fxDotScreen > 0.001 || p.fxFilmGrain > 0.001 || p.fxTiltShift > 0.001 ||
      p.fxRadialBlur > 0.001 || p.fxVignette > 0.001 || p.fxSwirl > 0.001 ||
      Math.abs(p.fxBarrel) > 0.001 || p.fxGridTile > 0.001 ||
      p.fxSepia > 0.001 || p.fxBleach > 0.001
    );
  }

  setFeatures(f: AudioFeatures): void {
    const now = performance.now();
    const dtSec = Math.min(0.05, Math.max(0, (now - this.lastFeatureAt) / 1000));
    this.lastFeatureAt = now;

    if (!this.hasSmoothedFeatures) {
      this.features = { ...f };
      this.hasSmoothedFeatures = true;
    } else {
      const smoothBand = (previous: number, target: number): number => {
        const clampedTarget = THREE.MathUtils.clamp(Math.abs(target) < 0.002 ? 0 : target, 0, 1);
        const speed = clampedTarget > previous ? 18 : 7;
        return previous + (clampedTarget - previous) * (1 - Math.exp(-speed * dtSec));
      };
      const bass = smoothBand(this.features.bass, f.bass);
      const mid = smoothBand(this.features.mid, f.mid);
      const treble = smoothBand(this.features.treble, f.treble);
      const level = (bass + mid + treble) / 3;
      const beat = Math.max(f.beat, this.features.beat * Math.exp(-8 * dtSec));
      this.features = {
        ...f,
        bass,
        mid,
        treble,
        level,
        beat,
      };
    }
    this.scene.setFeatures(this.features);
  }

  start(): void {
    if (this.disposed || this.rafId !== null || this.frameTimer !== null) return;
    const schedule = (): void => {
      if (document.hidden) {
        this.frameTimer = setTimeout(loop, 1000 / 30);
      } else {
        this.rafId = requestAnimationFrame(loop);
      }
    };
    const loop = (): void => {
      this.rafId = null;
      this.frameTimer = null;
      this.draw();
      schedule();
    };
    schedule();
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.frameTimer !== null) clearTimeout(this.frameTimer);
    this.rafId = null;
    this.frameTimer = null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stop();
    for (const cleanup of this.cleanupCallbacks.splice(0)) cleanup();
    this.altScene?.dispose();
    this.altScene = null;
    this.scene.dispose();
    this.bloomPass.dispose();
    this.feedbackPass.dispose();
    this.postFxPass.dispose();
    this.outputPass.dispose();
    this.composer.dispose();
    this.gl.dispose();
  }

  resetSceneState(): void {
    if (this.sceneType === "bioclast") {
      this.scene.params = this.params;
      this.scene.resetState();
      return;
    }
    if (this.altSceneType === this.sceneType) {
      this.altScene?.dispose();
      this.altScene = null;
      this.altSceneType = null;
      this.altCamBasePos = null;
      this.altCamBaseFov = null;
      this.altCamElapsed = 0;
    }
  }

  /** Apply (or clear) a vizz.fm-style cameraState override for the next alt scene update. */
  setAltCameraOverride(
    cs: { position: [number, number, number]; target: [number, number, number]; quaternion: [number, number, number, number] } | null,
  ): void {
    if (!cs) {
      this.clearAltCameraOverride();
      return;
    }
    this.altCamOverride = {
      position: new THREE.Vector3(cs.position[0], cs.position[1], cs.position[2]),
      target: new THREE.Vector3(cs.target[0], cs.target[1], cs.target[2]),
      quaternion: new THREE.Quaternion(cs.quaternion[0], cs.quaternion[1], cs.quaternion[2], cs.quaternion[3]),
    };
    // If an alt scene is already live, snap its base + camera now so the
    // change is visible immediately (without forcing a scene rebuild).
    if (this.altScene && this.altCamBasePos) {
      this.altCamBasePos.copy(this.altCamOverride.position);
      this.altScene.camera.position.copy(this.altCamOverride.position);
      this.altScene.camera.quaternion.copy(this.altCamOverride.quaternion);
      this.altCamElapsed = 0;
    }
    this.onAltCameraOverrideChange?.(true);
  }

  /** Drop any active vizz cameraState lock and let the standard rig take over. */
  clearAltCameraOverride(): void {
    const wasLocked = this.altCamOverride !== null;
    this.altCamOverride = null;
    this.altCamElapsed = 0;
    if (wasLocked) this.onAltCameraOverrideChange?.(false);
  }

  private updateAltSceneCamera(dt: number, motionT: number): void {
    if (!this.altScene || !this.altCamBasePos || this.altCamBaseFov === null) return;
    const cam = this.altScene.camera;
    const p = this.params;
    const f = this.features;
    this.altCamElapsed += dt;
    const t = this.visualSyncEpochSeconds === null ? this.altCamElapsed : motionT;
    const personality = this.cameraControlMode === "fly"
      ? "hallwayFly"
      : this.altScene.cameraPersonality;
    const cameraLerp = (amountAt60Fps: number): number => this.visualSyncEpochSeconds === null
      ? 1 - Math.pow(1 - amountAt60Fps, Math.min(3, dt * 60))
      : 1;
      const cameraMotionOn =
        p.cameraSpin > 0 ||
        p.camAutoRotateH !== 0 ||
        p.camAutoRotateV !== 0 ||
        p.camAutoRoll !== 0 ||
        p.camPanAmount > 0 ||
        p.camZoomAmount > 0 ||
        p.camFovAmount > 0;
      const motionScale = cameraMotionOn ? 1 : 0;

    const base = this.altCamBasePos;
    const baseRadius = Math.max(2, Math.hypot(base.x, base.z));
      const beatLift = motionScale * this.beatLevel * (0.8 + p.bassGravity * 0.2);
    const fovOsc =
        cameraMotionOn && p.camFovAmount > 0 && p.camFovPeriod > 0
        ? Math.sin((motionT / p.camFovPeriod) * Math.PI * 2) * p.camFovAmount
        : 0;

    if (personality === "staticParallax") {
      // Preserve scene-author framing. Add only gentle drift/bob and FOV breathing.
        const driftX = motionScale * Math.sin(t * 0.18) * (0.06 + p.camPanAmount * 0.8);
        const driftY = motionScale * Math.sin(t * 0.24) * (0.04 + p.camPanAmount * 0.5);
        const bob = motionScale * Math.sin(t * 0.38) * (0.025 + beatLift * 0.06);
      this.altCamDesired.set(base.x + driftX, base.y + driftY + bob, base.z);
      cam.position.lerp(this.altCamDesired, cameraLerp(0.035));
      this.altCamTarget.set(driftX * 0.4, driftY * 0.4, 0);
      cam.lookAt(this.altCamTarget);
    } else if (personality === "hallwayFly") {
      // Forward dolly with sway. Camera stays roughly where the scene placed
      // it, but oscillates along Z for the corridor feel + small lateral sway.
      const dollyZ =
          motionScale * Math.sin(t * (0.18 + p.flythroughSpeed * 0.18)) * (0.55 + p.camZoomAmount * 2.2);
        const sway = motionScale * Math.sin(t * 0.28) * (0.16 + p.camPanAmount * 0.9);
        const lift = motionScale * (Math.sin(t * 0.2) * 0.12 + beatLift * 0.08);
      this.altCamDesired.set(base.x + sway, base.y + lift, base.z + dollyZ);
      cam.position.lerp(this.altCamDesired, cameraLerp(0.04));
      this.altCamTarget.set(sway * 0.3, lift * 0.3, 0);
      cam.lookAt(this.altCamTarget);
    } else if (this.altCamOverride) {
      // Authored vizz framing: orbit GENTLY around the saved target, with the
      // saved position as the radius/elevation reference. This preserves the
      // unique cinematic angle of each preset while still feeling alive.
      const ov = this.altCamOverride;
      const offX = base.x - ov.target.x;
      const offY = base.y - ov.target.y;
      const offZ = base.z - ov.target.z;
      const radius = Math.max(0.5, Math.hypot(offX, offZ));
      const baseAzimuth = Math.atan2(offZ, offX);
        const spinRate = motionScale * (0.018 + p.cameraSpin * 0.06 + p.camAutoRotateH * 3);
      const azimuth = baseAzimuth + t * spinRate;
        const bob = motionScale * Math.sin(t * 0.24) * (0.025 + beatLift * 0.04);
      const zoomOsc =
          cameraMotionOn && p.camZoomAmount > 0 && p.camZoomPeriod > 0
          ? Math.sin((motionT / p.camZoomPeriod) * Math.PI * 2) * p.camZoomAmount * 0.25
          : 0;
      const r = radius * Math.max(0.7, 1 - (p.cameraZoom - 1) * 0.25 + zoomOsc);
      this.altCamTarget.copy(ov.target);
      this.altCamDesired.set(
        ov.target.x + Math.cos(azimuth) * r,
        ov.target.y + offY + bob,
        ov.target.z + Math.sin(azimuth) * r,
      );
      cam.position.lerp(this.altCamDesired, cameraLerp(0.04));
      cam.lookAt(this.altCamTarget);
    } else {
      // "orbit" or "closeOrbit" — full orbital rig.
      const close = personality === "closeOrbit";
      const spinRate =
          motionScale * ((close ? 0.08 : 0.025) + p.cameraSpin * (close ? 0.18 : 0.12) + p.camAutoRotateH * 4);
      // Orbit relative to the scene-authored camera direction. Starting every
      // alt scene from +X turns +Z-authored sheets and shells edge-on.
      const baseAzimuth = Math.atan2(base.z, base.x);
      const azimuth = baseAzimuth + (this.outputFill > 1.001 ? 0 : p.cameraOrbit + t * spinRate);
      const tiltBias = (p.cameraTilt - 0.5) * (close ? 0.6 : 0.9);
        const bob = motionScale * Math.sin(t * (0.22 + p.camAutoRotateV * 3)) * (0.08 + p.camPanAmount * 0.4);
      const zoomOsc =
          cameraMotionOn && p.camZoomAmount > 0 && p.camZoomPeriod > 0
          ? Math.sin((motionT / p.camZoomPeriod) * Math.PI * 2) * p.camZoomAmount * 0.18
          : 0;
      const radiusScale = THREE.MathUtils.clamp(
        (close ? 0.85 : 1) / Math.max(0.15, p.cameraZoom) + zoomOsc,
        close ? 0.5 : 0.32,
        close ? 4.5 : 6.0,
      );
      const radius = baseRadius * radiusScale;
      const verticalRadius = Math.max(0.5, radius * (close ? 0.18 : 0.25));
      const targetJitter = motionScale * ((close ? 0.02 : 0.045) + p.camPanAmount * (close ? 0.35 : 0.7));
      this.altCamTarget.set(
        Math.sin(t * 0.21) * targetJitter,
        Math.sin(t * 0.17) * targetJitter * 0.5,
        Math.cos(t * 0.19) * targetJitter,
      );
      const polar = Math.PI / 2 - tiltBias;
      const y =
        base.y +
        Math.cos(polar) * verticalRadius +
        bob +
        beatLift * f.bass * 0.12;
      const horiz = Math.max(1, Math.sin(polar) * radius);
      const x = this.altCamTarget.x + Math.cos(azimuth) * horiz;
      const z = this.altCamTarget.z + Math.sin(azimuth) * horiz;
      this.altCamDesired.set(x, y, z);
      cam.position.lerp(this.altCamDesired, cameraLerp(close ? 0.055 : 0.04));
      cam.lookAt(this.altCamTarget);
    }

    const nextFov = THREE.MathUtils.clamp(this.altCamBaseFov + fovOsc, 30, 95);
    if (Math.abs(nextFov - cam.fov) > 0.001) {
      cam.fov = nextFov;
      cam.updateProjectionMatrix();
    }
  }

  private draw(): void {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastFrameAt) / 1000);
    this.lastFrameAt = now;
    this.frameMs = this.frameMs * 0.92 + dt * 1000 * 0.08;
    this.adaptQuality();
    const motionT = this.visualSyncEpochSeconds === null
      ? (now - this.startedAt) / 1000
      : Math.max(0, Date.now() / 1000 - this.visualSyncEpochSeconds);
    this.updateAutoDirector(now);

    // Keep features in sync (no-op if already set this frame).
    this.scene.setFeatures(this.features);
    this.scene.params = this.params;
    this.scene.cameraModeOverride = this.cameraControlMode === "fly" ? "flythrough" : null;

    // Ensure the right alt scene exists when a non-bioclast type is active.
    if (this.sceneType !== "bioclast" && this.sceneType !== this.altSceneType) {
      this.altScene?.dispose();
      const w = (this.canvas.clientWidth || window.innerWidth) / this.outputView.width;
      const h = (this.canvas.clientHeight || window.innerHeight) / this.outputView.height;
      this.altScene = createAltScene(this.sceneType as AltSceneType, w, h);
      this.altSceneType = this.sceneType as AltSceneType;
      this.altCamBasePos = this.altScene.camera.position.clone();
      this.altCamBaseFov = this.altScene.camera.fov;
      this.altCamElapsed = 0;
      // If a preset has supplied a vizz cameraState, snap the base to it so
      // the orbital rig pivots around the authored framing instead of (0,0,0).
      if (this.altCamOverride) {
        this.altCamBasePos.copy(this.altCamOverride.position);
        this.altScene.camera.position.copy(this.altCamOverride.position);
        this.altScene.camera.quaternion.copy(this.altCamOverride.quaternion);
      }
      // Wire the new alt scene into the renderPass for the post-fx chain.
      this.renderPass.scene = this.altScene.scene;
      this.renderPass.camera = this.altScene.camera;
    } else if (this.sceneType === "bioclast" && this.altSceneType !== null) {
      // Switching back to bioclast.
      this.altScene?.dispose();
      this.altScene = null;
      this.altSceneType = null;
      this.altCamBasePos = null;
      this.altCamBaseFov = null;
      this.altCamElapsed = 0;
      this.renderPass.scene = this.scene.scene;
      this.renderPass.camera = this.scene.camera;
    }

    // Apply modulations BEFORE updating the active scene. Snapshot then
    // restore so the user's slider values aren't permanently overwritten.
    let hadMods = false;
    for (const k of Object.keys(this.modulations) as (keyof BioclastParams)[]) {
      const m = this.modulations[k];
      if (m && m.enabled && m.amount > 0) {
        const v = (this.params as unknown as Record<string, unknown>)[k as string];
        if (typeof v === "number") {
          this.paramSnapshot[k] = v as never;
          hadMods = true;
        }
      }
    }
    if (hadMods) {
      applyModulations(this.params, this.modulations, this.features, motionT);
    }

    if (this.altScene) {
      this.altScene.setVisualTime?.(this.visualSyncEpochSeconds === null ? null : motionT);
      this.altScene.setOutputSpread?.(this.outputFill, this.outputFillY);
      this.altScene.update(dt, this.params, this.features);
    } else {
      this.scene.update(dt);
    }

    // Restore snapshot so the next frame starts from user-set values.
    if (hadMods) {
      for (const k of Object.keys(this.paramSnapshot) as (keyof BioclastParams)[]) {
        const v = this.paramSnapshot[k];
        if (typeof v === "number") {
          (this.params as unknown as Record<string, number>)[k as string] = v;
        }
      }
      this.paramSnapshot = {};
    }

    if (this.altScene && this.cameraControlMode !== "free") {
      this.updateAltSceneCamera(dt, motionT);
    }

    // Apply programmatic camera motion (vizz.fm-style auto-cam) AFTER the
    // scene places the camera. These layer on top of orbit/flythrough modes
    // without fighting them — we just add offsets to the already-positioned
    // camera. Skip the entire block when nothing is enabled.
    const cam = this.altScene ? this.altScene.camera : this.scene.camera;
    const p = this.params;
    if (!this.altScene && this.cameraControlMode === "auto") {
      if (
        p.camAutoRotateH !== 0 ||
        p.camAutoRotateV !== 0 ||
        p.camAutoRoll !== 0
      ) {
        cam.rotation.y += p.camAutoRotateH * dt;
        cam.rotation.x += p.camAutoRotateV * dt;
        cam.rotation.z += p.camAutoRoll * dt;
      }
      if (p.camFovAmount > 0 && p.camFovPeriod > 0) {
        const phase = (motionT / p.camFovPeriod) * Math.PI * 2;
          const nextFov = THREE.MathUtils.clamp(cam.fov + Math.sin(phase) * p.camFovAmount, 30, 95);
          if (Math.abs(nextFov - cam.fov) > 0.001) {
            cam.fov = nextFov;
            cam.updateProjectionMatrix();
          }
      }
      if (p.camZoomAmount > 0 && p.camZoomPeriod > 0) {
        // Mild dolly along camera forward vector — 1 unit world max.
        const phase = (motionT / p.camZoomPeriod) * Math.PI * 2;
        const dolly = Math.sin(phase) * p.camZoomAmount;
        cam.translateZ(-dolly);
      }
      if (p.camPanAmount > 0 && p.camPanPeriod > 0) {
        const phase = (motionT / p.camPanPeriod) * Math.PI * 2;
        cam.translateX(Math.sin(phase) * p.camPanAmount);
      }
    }
    if (this.cameraControlMode === "free") this.applyFreeCamera();
    this.applyOutputTileView(cam);
    if (this.onCameraChange && now - this.lastCameraTelemetryAt >= 250) {
      this.lastCameraTelemetryAt = now;
      this.onCameraChange(this.getCameraState());
    }

    // Sync bloom + feedback params each frame (cheap; lets the UI
    // mutate live). When BOTH bloom AND feedback are off, skip the
    // composer entirely and render the scene directly to the canvas
    // for max perf — otherwise route through the composer chain.
    const fxActive = this.isCurrentPostFxActive();
    this.bloomPass.enabled = this.params.bloomEnabled;
    this.feedbackPass.enabled = this.params.feedbackStrength > 0.001;
    this.postFxPass.enabled = fxActive;
    const useComposer =
      this.params.bloomEnabled || this.params.feedbackStrength > 0 || fxActive;
    this.lastUseComposer = useComposer;
    if (useComposer) {
      this.bloomPass.strength = this.params.bloomStrength;
      this.bloomPass.radius = this.params.bloomRadius;
      this.bloomPass.threshold = this.params.bloomThreshold;
      // Push live feedback knobs into the pass.
      this.feedbackPass.params.strength = this.params.feedbackStrength;
      this.feedbackPass.params.zoom = this.params.feedbackZoom;
      this.feedbackPass.params.rotate = this.params.feedbackRotate;
      this.feedbackPass.params.driftX = this.params.feedbackDriftX;
      this.feedbackPass.params.driftY = this.params.feedbackDriftY;
      this.feedbackPass.params.decay = this.params.feedbackDecay;
      // Push post-FX knobs.
      const fp = this.postFxPass.params;
      fp.enabled = this.params.postFxEnabled;
      fp.rgbShift = this.params.fxRgbShift;
      fp.glitch = this.params.fxGlitch;
      fp.crt = this.params.fxCrt;
      fp.kaleidoscope = this.params.fxKaleidoscope;
      fp.pixelate = this.params.fxPixelate;
      fp.dither = this.params.fxDither;
      fp.dotScreen = this.params.fxDotScreen;
      fp.filmGrain = this.params.fxFilmGrain;
      fp.tiltShift = this.params.fxTiltShift;
      fp.radialBlur = this.params.fxRadialBlur;
      fp.vignette = this.params.fxVignette;
      fp.swirl = this.params.fxSwirl;
      fp.barrel = this.params.fxBarrel;
      fp.kaleidoscopeSegments = this.params.fxKaleidoscopeSegments;
      fp.gridTile = this.params.fxGridTile;
      fp.gridTileColumns = this.params.fxGridTileColumns;
      // Extended vizz-parity params.
      fp.rgbShiftAngle = this.params.fxRgbShiftAngle;
      fp.rgbShiftOpacity = this.params.fxRgbShiftOpacity;
      fp.glitchSpeed = this.params.fxGlitchSpeed;
      fp.glitchBlock = this.params.fxGlitchBlock;
      fp.glitchOpacity = this.params.fxGlitchOpacity;
      fp.crtCurvature = this.params.fxCrtCurvature;
      fp.crtScanlineIntensity = this.params.fxCrtScanlineIntensity;
      fp.crtRgbOffset = this.params.fxCrtRgbOffset;
      fp.crtOpacity = this.params.fxCrtOpacity;
      fp.kaleidoscopeRotation = this.params.fxKaleidoscopeRotation;
      fp.kaleidoscopeCenterX = this.params.fxKaleidoscopeCenterX;
      fp.kaleidoscopeCenterY = this.params.fxKaleidoscopeCenterY;
      fp.kaleidoscopeOpacity = this.params.fxKaleidoscopeOpacity;
      fp.pixelateOpacity = this.params.fxPixelateOpacity;
      fp.ditherLevels = this.params.fxDitherLevels;
      fp.ditherPixelScale = this.params.fxDitherPixelScale;
      fp.ditherOpacity = this.params.fxDitherOpacity;
      fp.dotScreenAngle = this.params.fxDotScreenAngle;
      fp.dotScreenCenterX = this.params.fxDotScreenCenterX;
      fp.dotScreenCenterY = this.params.fxDotScreenCenterY;
      fp.dotScreenOpacity = this.params.fxDotScreenOpacity;
      fp.filmGrainScanlineIntensity = this.params.fxFilmGrainScanlineIntensity;
      fp.filmGrainScanlineCount = this.params.fxFilmGrainScanlineCount;
      fp.filmGrainGrayscale = this.params.fxFilmGrainGrayscale;
      fp.filmGrainOpacity = this.params.fxFilmGrainOpacity;
      fp.tiltShiftFocusPos = this.params.fxTiltShiftFocusPos;
      fp.tiltShiftFocusWidth = this.params.fxTiltShiftFocusWidth;
      fp.tiltShiftFalloff = this.params.fxTiltShiftFalloff;
      fp.tiltShiftOpacity = this.params.fxTiltShiftOpacity;
      fp.radialBlurCenterX = this.params.fxRadialBlurCenterX;
      fp.radialBlurCenterY = this.params.fxRadialBlurCenterY;
      fp.radialBlurInnerRadius = this.params.fxRadialBlurInnerRadius;
      fp.radialBlurFalloff = this.params.fxRadialBlurFalloff;
      fp.radialBlurOpacity = this.params.fxRadialBlurOpacity;
      fp.vignetteSize = this.params.fxVignetteSize;
      fp.vignetteOpacity = this.params.fxVignetteOpacity;
      fp.swirlAngle = this.params.fxSwirlAngle;
      fp.swirlRadius = this.params.fxSwirlRadius;
      fp.swirlFalloff = this.params.fxSwirlFalloff;
      fp.swirlCenterX = this.params.fxSwirlCenterX;
      fp.swirlCenterY = this.params.fxSwirlCenterY;
      fp.swirlOpacity = this.params.fxSwirlOpacity;
      fp.barrelAmountX = this.params.fxBarrelAmountX;
      fp.barrelAmountY = this.params.fxBarrelAmountY;
      fp.barrelCenterX = this.params.fxBarrelCenterX;
      fp.barrelCenterY = this.params.fxBarrelCenterY;
      fp.barrelZoom = this.params.fxBarrelZoom;
      fp.barrelChroma = this.params.fxBarrelChroma;
      fp.barrelVignette = this.params.fxBarrelVignette;
      fp.barrelVignetteSize = this.params.fxBarrelVignetteSize;
      fp.barrelOpacity = this.params.fxBarrelOpacity;
      fp.gridTileRows = this.params.fxGridTileRows;
      fp.gridTileGap = this.params.fxGridTileGap;
      fp.gridTileMirror = this.params.fxGridTileMirror;
      fp.gridTileCenterX = this.params.fxGridTileCenterX;
      fp.gridTileCenterY = this.params.fxGridTileCenterY;
      fp.gridTileOpacity = this.params.fxGridTileOpacity;
      fp.sepia = this.params.fxSepia;
      fp.sepiaOpacity = this.params.fxSepiaOpacity;
      fp.bleach = this.params.fxBleach;
      fp.bleachOpacity = this.params.fxBleachOpacity;
      this.postFxPass.syncUniforms(motionT, this.scene.beatLevel);

      // GPGPU alt scenes must advance their simulation BEFORE the
      // composer binds its writeBuffer; otherwise their setRenderTarget
      // calls stomp the composer's FBO binding and the particles
      // render to the canvas backbuffer instead of the composer input.
      this.altScene?.prerender?.(this.gl, dt, (now - this.startedAt) / 1000);
      this.composer.render(dt);
    } else {
      this.altScene?.prerender?.(this.gl, dt, (now - this.startedAt) / 1000);
      const renderScene = this.altScene ? this.altScene.scene : this.scene.scene;
      const renderCam = this.altScene ? this.altScene.camera : this.scene.camera;
      this.gl.render(renderScene, renderCam);
    }
  }

  /** Latest backdrop tint computed by the active scene. */
  get backdrop(): { r: number; g: number; b: number; alpha: number; blurPx: number } {
    return this.altScene ? this.altScene.backdrop : this.scene.backdrop;
  }

  /** Latest beat envelope (0..1). Useful for beat-locked UI behaviors. */
  get beatLevel(): number {
    return this.altScene ? this.altScene.beatLevel : this.scene.beatLevel;
  }

  getBioclastDiagnostics(): BioclastDiagnostics | null {
    return this.sceneType === "bioclast" ? this.scene.getDiagnostics() : null;
  }

  getDiagnostics(): RendererDiagnostics {
    return {
      rendererPixelRatio: this.gl.getPixelRatio(),
      composerPixelRatio: this.composerPixelRatio,
      composerActive: this.lastUseComposer,
      feedbackPassEnabled: this.feedbackPass.enabled,
      postFxPassEnabled: this.postFxPass.enabled,
      altScene: this.altScene?.getDiagnostics?.(),
    };
  }

  /** Returns the underlying canvas element (for compositors / DOM observers). */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /** Returns a stream that mirrors the canvas at a given fps for recording. */
  captureStream(fps = 60): MediaStream {
    return this.canvas.captureStream(fps);
  }

  /** Snapshot the current frame as a PNG blob. */
  snapshot(): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.canvas.toBlob((b) => resolve(b), "image/png");
    });
  }
}
